import { ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useResizeObserver } from '@vueuse/core'

type UseIsTruncatedOptions = {
    /**
     * Whether to observe the element. When `false` no `ResizeObserver` is created
     * and `isTruncated` stays `false` — use it to avoid the observer cost on
     * instances that don't need truncation detection. Evaluated once on setup, so
     * pass a static flag.
     * @default true
     */
    enabled?: boolean
}

/**
 * Detects whether an element's content is visually truncated by CSS
 * (e.g. `truncate` / `text-overflow: ellipsis` or `-webkit-line-clamp`).
 *
 * @example
 * const element = ref<HTMLElement>()
 * const { isTruncated } = useIsTruncated(element, { enabled })
 *
 * Skip the observer when truncation isn't needed:
 * const { isTruncated } = useIsTruncated(element, { enabled: props.truncate })
 */
export const useIsTruncated = (
    target: MaybeRefOrGetter<HTMLElement | undefined | null>,
    { enabled = true }: UseIsTruncatedOptions = {},
) => {
    const isTruncated = ref(false)

    const checkTruncation = () => {
        const element = toValue(target)

        if (!element) {
            isTruncated.value = false
            return
        }

        isTruncated.value = element.scrollWidth > element.clientWidth
    }

    if (enabled) {
        useResizeObserver(target, checkTruncation)
    }

    return { isTruncated, checkTruncation }
}
